import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';

import { Reservation } from 'src/models/schedule-weekly-view.model';
import { ReservationPopoverComponent } from '../reservation-popover/reservation-popover.component';
import { ReservationInteractionService } from '../../services/reservation-interaction.service';
import { WindowService } from 'src/shared/providers/window.service';

/**
 * A reservation drawn as a horizontal swim-lane bar in the enhanced monthly
 * view, spanning the stay's full date range within a week row. Styled with
 * the same surface colours as the weekly view's reservation cards; hover
 * (desktop) or tap (mobile) opens the same reservation popover too.
 */
@Component({
  selector: 'app-reservation-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './reservation-bar.component.html',
  styleUrls: ['./reservation-bar.component.scss'],
  imports: [CommonModule, MatMenuModule, ReservationPopoverComponent],
})
export class ReservationBarComponent implements OnDestroy {
  private readonly interaction = inject(ReservationInteractionService);
  private readonly windowService = inject(WindowService);

  @Input() reservation!: Reservation;
  @Input() address?: string;
  @Input() addressId?: number | string;
  @Input() continuesLeft = false;
  @Input() continuesRight = false;
  @Input() showLabel = true;

  @Output() readonly barClick = new EventEmitter<Reservation>();

  @ViewChild(MatMenuTrigger) trigger?: MatMenuTrigger;

  logoFailed = false;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly HOVER_OPEN_DELAY = 120;
  private readonly HOVER_CLOSE_DELAY = 180;

  ngOnDestroy(): void {
    this.clearTimers();
  }

  get isDesktop(): boolean {
    return this.windowService.isDesktopRes;
  }

  get isInquiry(): boolean {
    return this.reservation?.state === 'inquiry';
  }

  get isHold(): boolean {
    return this.reservation?.state === 'hold';
  }

  get label(): string {
    const guest = this.reservation?.guestName?.trim();
    const platform = this.reservation?.sourceName?.trim();
    const name = guest && platform
      ? `${guest} • ${platform}`
      : guest || platform || this.reservation?.title || '';
    return this.isHold ? `Hold • ${name}` : name;
  }

  get logoUrl(): string | undefined {
    if (this.logoFailed) {
      return undefined;
    }
    return this.reservation?.sourceLogoUrl;
  }

  onLogoError(): void {
    this.logoFailed = true;
  }

  private clearTimers(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  onBarMouseEnter(): void {
    if (!this.isDesktop) {return;}
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    if (this.trigger?.menuOpen) {return;}
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.trigger?.openMenu();
    }, this.HOVER_OPEN_DELAY);
  }

  onBarMouseLeave(): void {
    if (!this.isDesktop) {return;}
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    this.scheduleClose();
  }

  private scheduleClose(): void {
    if (this.closeTimer) {return;}
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.trigger?.closeMenu();
    }, this.HOVER_CLOSE_DELAY);
  }

  onPopoverHoverChange(hovered: boolean): void {
    if (!this.isDesktop) {return;}
    if (hovered) {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    } else {
      this.scheduleClose();
    }
  }

  onBarClick(event: Event): void {
    event.stopPropagation();
    this.barClick.emit(this.reservation);
    this.trigger?.openMenu();
  }

  handleGuidebookFromPopover(): void {
    this.trigger?.closeMenu();
    this.interaction.openGuidebook(this.reservation, {
      address: this.address,
      addressId: this.addressId,
    });
  }

  handleDetailsFromPopover(): void {
    this.trigger?.closeMenu();
    this.interaction.openDetail(this.reservation, {
      address: this.address,
      addressId: this.addressId,
    });
  }

  handleMessageFromPopover(): void {
    this.trigger?.closeMenu();
    void this.interaction.openMessageThread(this.reservation);
  }
}
